import { UUID } from 'crypto';
import NewlyAssignedChatIcon from '../../assets/icons/whatsAppChatbot/NewlyAssignedChatIcon';
import UnreadMessageChatIcon from '../../assets/icons/whatsAppChatbot/UnreadMessageChatIcon';
import ChatReadIcon from '../../assets/icons/whatsAppChatbot/ChatReadIcon';
import ChatFailedIcon from '../../assets/icons/whatsAppChatbot/ChatFailedIcon';
import MessageDeliveredIcon from '../../assets/icons/whatsAppChatbot/MessageDeliveredIcon';
import MessageSentIcon from '../../assets/icons/whatsAppChatbot/MessageSentIcon';
import MessageSentInitiatedChatIcon from '../../assets/icons/whatsAppChatbot/MessageSentInitiatedChatIcon';
import React from 'react';
import PleaseReplyIcon from '../../assets/icons/whatsAppChatbot/PleaseReplyIcon';
import { Dispatch } from '@reduxjs/toolkit';

export enum MessageType {
  STATUS = 'STATUS',
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  TEMPLATE = 'AGENT_TRIGGERED_TEMPLATE',
  PDF = 'PDF',
  DOCUMENT = 'DOCUMENT'
}

export enum ChatSender {
  USER = 'USER',
  BOT = 'BOT',
  HRC_AGENT = 'HRC_AGENT',
  TELE_AGENT = 'TELE_AGENT',
  AD_HOC = 'AD_HOC'
}

export interface MediaData {
  media_url: string;
  caption?: string;
  file_name?: string;
  file_size?: number;
}

export const ChatSenderStatusText = {
  [ChatSender.BOT]: 'Bot Assigned',
  [ChatSender.HRC_AGENT]: 'HRC Agent Assigned',
  [ChatSender.TELE_AGENT]: 'Tele Agent Assigned',
  [ChatSender.AD_HOC]: 'Adhoc Assigned'
};

export enum MessageStatus {
  UN_PROCESSED = 'UN_PROCESSED',
  PROCESSED = 'PROCESSED'
}
export enum ChatAssignmentStatusEnum {
  ACTIVE = 'ACTIVE',
  REVOKED = 'REVOKED'
}

export interface IChatListItem {
  customerName: string;
  loanAccountNumber: string;
  messageContent: string;
  messageTimestamp: number;
  messageType: MESSAGE_TYPE;
  phoneNumber: string;
  tag: CHAT_MESSAGE_TAG;
  updatedAtEpoch: Date;
  referenceId: string;
  lastUserMessageTimeStamp: number;
  customerId: string;
  chatAssignmentStatus?: ChatAssignmentStatusEnum;
}

export enum ReplySendingStatus {
  IDLE = 'IDLE',
  SENDING = 'SENDING',
  LOADING = 'LOADING'
}

export interface ITriggerTemplateDetails {
  templateId: string;
  templateName: string;
  templateMessage: string;
  variableMap: { [key: string]: string };
}

export enum DeliveryStatus {
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
  SENT_TO_CUSTOMER = 'SENT_TO_CUSTOMER',
  DELIVERED = 'DELIVERED',
  IN_PROGRESS = 'IN_PROGRESS',
  NOT_DELIVERED = 'NOT_DELIVERED',
  FAILED = 'FAILED',
  INITIATED = 'INITIATED',
  EXPIRED = 'EXPIRED',
  EXTERNAL_FAILURE = 'EXTERNAL_FAILURE',
  ACKNOWLEDGED_BY_CUSTOMER = 'ACKNOWLEDGED_BY_CUSTOMER',
  DROPPED = 'DROPPED',
  UNAUTHORISED = 'UNAUTHORISED',
  AGENT_NOT_READY = 'AGENT_NOT_READY',
  RECEIVED = 'RECEIVED'
}

export enum DeliveryStatusText {
  SEEN = 'Seen',
  DELIVERED = 'Delivered',
  SENT = 'Sent',
  WAITING = 'Waiting',
  FAILED = 'Failed'
}

export const DeliveryStatusMapText = {
  [DeliveryStatus.SCHEDULED]: DeliveryStatusText.WAITING,
  [DeliveryStatus.SENT]: DeliveryStatusText.SENT,
  [DeliveryStatus.SENT_TO_CUSTOMER]: DeliveryStatusText.SENT,
  [DeliveryStatus.DELIVERED]: DeliveryStatusText.DELIVERED,
  [DeliveryStatus.IN_PROGRESS]: DeliveryStatusText.WAITING,
  [DeliveryStatus.NOT_DELIVERED]: DeliveryStatusText.FAILED,
  [DeliveryStatus.FAILED]: DeliveryStatusText.FAILED,
  [DeliveryStatus.INITIATED]: DeliveryStatusText.WAITING,
  [DeliveryStatus.EXPIRED]: DeliveryStatusText.FAILED,
  [DeliveryStatus.EXTERNAL_FAILURE]: DeliveryStatusText.FAILED,
  [DeliveryStatus.ACKNOWLEDGED_BY_CUSTOMER]: DeliveryStatusText.SEEN,
  [DeliveryStatus.DROPPED]: DeliveryStatusText.FAILED,
  [DeliveryStatus.UNAUTHORISED]: DeliveryStatusText.FAILED,
  [DeliveryStatus.AGENT_NOT_READY]: DeliveryStatusText.FAILED,
  [DeliveryStatus.RECEIVED]: DeliveryStatusText.SEEN
};

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC'
}

export const AvatarBlueStyles: React.CSSProperties = {
  backgroundColor: '#E5F3FF',
  color: '#0085FF',
  border: '1px solid #CCE7FF',
  fontSize: '13px',
  objectFit: 'cover'
};

export const AvatarVioletStyles = {
  backgroundColor: '#8B2CE91A',
  color: '#8B2CE9',
  border: '1px solid #8B2CE9',
  fontSize: '13px'
};

export const DEFAULT_SCREEN_IMG_PATH = 'src/assets/images/EmptyChatPhone.png';

export enum EmptyScreenMsg {
  DEFAULT_SCREEN = 'DEFAULT_SCREEN',
  NO_CHATS_ASSIGNED = 'NO_CHATS_ASSIGNED',
  ERROR_PAGE = 'ERROR_PAGE'
}

export const lightGreyscaleColor = '#969696';

export const msgFailedColor = '#E92C2C';

export const WHATSAPP_CHATBOT_PAGINATE_API_BATCH_SIZE = 20;
export enum MESSAGE_TYPE {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE'
}

export enum CHAT_MESSAGE_TAG {
  NEWLY_ASSIGNED = 'NEWLY_ASSIGNED',
  MESSAGE_RECEIVED_UN_READ = 'MESSAGE_RECEIVED_UN_READ',
  MESSAGE_RECEIVED_READ = 'MESSAGE_RECEIVED_READ',
  MESSAGE_SENT_FAILED = 'MESSAGE_SENT_FAILED',
  MESSAGE_SENT_ACKNOWLEDGED_BY_CUSTOMER = 'MESSAGE_SENT_ACKNOWLEDGED_BY_CUSTOMER',
  MESSAGE_SENT_DELIVERED = 'MESSAGE_SENT_DELIVERED',
  MESSAGE_SENT_SENT_TO_CUSTOMER = 'MESSAGE_SENT_SENT_TO_CUSTOMER',
  MESSAGE_SENT_INITIATED = 'MESSAGE_SENT_INITIATED'
}

export type ChatBucket = {
  [key: string]: Record<string, IChatListItem>;
};

export const CHAT_MESSAGE_TAG_PRIORITY_LIST = [
  CHAT_MESSAGE_TAG.NEWLY_ASSIGNED,
  CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_UN_READ,
  CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_READ,
  CHAT_MESSAGE_TAG.MESSAGE_SENT_FAILED,
  CHAT_MESSAGE_TAG.MESSAGE_SENT_ACKNOWLEDGED_BY_CUSTOMER,
  CHAT_MESSAGE_TAG.MESSAGE_SENT_DELIVERED,
  CHAT_MESSAGE_TAG.MESSAGE_SENT_SENT_TO_CUSTOMER,
  CHAT_MESSAGE_TAG.MESSAGE_SENT_INITIATED
];

export interface WhatsappChatbotMessages {
  referenceId: string;
  createdAt: Date;
  updatedAt?: Date;
  messageId: UUID;
  messageGroupId?: number;
  messageGroupOrderId?: number;
  sender: ChatSender;
  senderReferenceId: string;
  content: string;
  messageStatus?: MessageStatus;
  deliveryStatus: DeliveryStatus;
  timestamp?: number;
  ttl?: number;
  messageType: MessageType;
}

export interface WhatsappChatbotSelectedChat {
  chatRefId: UUID | '';
  chatEndDate: Date | null;
  customerNameInitials: string;
}

export const chatsTagsIconsMap = {
  [CHAT_MESSAGE_TAG.NEWLY_ASSIGNED]: NewlyAssignedChatIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_UN_READ]: UnreadMessageChatIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_READ]: PleaseReplyIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_FAILED]: ChatFailedIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_ACKNOWLEDGED_BY_CUSTOMER]: ChatReadIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_DELIVERED]: MessageDeliveredIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_SENT_TO_CUSTOMER]: MessageSentIcon,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_INITIATED]: MessageSentInitiatedChatIcon
};

export const chatTagsFilterTextMap = {
  [CHAT_MESSAGE_TAG.NEWLY_ASSIGNED]: 'New',
  [CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_UN_READ]: 'Unread',
  [CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_READ]: 'Please Reply',
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_FAILED]: 'Failed',
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_ACKNOWLEDGED_BY_CUSTOMER]: 'Seen',
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_DELIVERED]: 'Delivered',
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_SENT_TO_CUSTOMER]: 'Sent',
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_INITIATED]: 'Waiting'
};

export const quickFiltersDefaultValues = {
  [CHAT_MESSAGE_TAG.NEWLY_ASSIGNED]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_UN_READ]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_RECEIVED_READ]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_FAILED]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_ACKNOWLEDGED_BY_CUSTOMER]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_DELIVERED]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_SENT_TO_CUSTOMER]: false,
  [CHAT_MESSAGE_TAG.MESSAGE_SENT_INITIATED]: false
};

export const MAX_CHAT_RELOAD = 3;

export interface LoanAndUserDetails {
  lan: string;
  loanAmount: number;
  phone: string;
  phoneCustomId?: string;
  totalDue: number;
  loanType: string;
  name: string;
  candidateId: string;
  currentDpd: number;
}

export const CURRENT_CHAT_REF_ID_KEY = 'currentChatRefId';
export const HRC_CHAT_AGENT_ONLINE_STATUS = 'HRC_CHAT_AGENT_ONLINE_STATUS';

export interface ICustomerSelfieRecord {
  isSelfieAvailable: boolean;
  preSignedUri: string;
  updatedAtEpoch: number;
}

export enum AGENT_STATUS {
  ONLINE = 'online',
  OFFLINE = 'offline'
}

export enum FETCH_CHATS_CALLBACK_TYPE {
  NEW_CHATS,
  MORE_CHATS,
  UPDATE_CHATS
}

export interface IFetchChatsHelper {
  chatReferenceId: string;
  createdAt?: Date;
  sortOrder?: SortOrder;
  successCallbackDispatch: FETCH_CHATS_CALLBACK_TYPE;
  isScrollBottom?: boolean;
  isCallMarkRead?: boolean;
  failureCallback?: () => void;
  dispatch: Dispatch;
}

export interface IFetchNewChatsParams {
  chatReferenceId: string;
  sortOrder?: SortOrder;
  isCallMarkRead: boolean;
}

export interface IPlaceholderData {
  [key: string]: string;
}

export interface IFetchMoreChatsParams {
  chatReferenceId: string;
  createdAt?: Date;
  sortOrder?: SortOrder;
}

export interface IFetchUpdatedChatsParams {
  chatReferenceId: string;
  createdAt?: Date;
  sortOrder?: SortOrder;
  isCallMarkRead: boolean;
  messages: Array<WhatsappChatbotMessages>;
}

export interface EncodeQueryParams {
  chatReferenceId: string;
  createdAt?: Date;
  sortOrder?: SortOrder;
}

export interface SendChatMessage {
  date: Date;
  chatSender: ChatSender;
  chatReferenceId: string;
  messageType: MessageType;
  messageContent: string;
  agentReferenceId: string;
  setChatMessageContent: (value: React.SetStateAction<string>) => void;
  placeholderData?: IPlaceholderData;
  templateId?: string;
}

export interface SendChatMessageWithImage {
  date: Date;
  chatSender: ChatSender;
  chatReferenceId: string;
  messageType: MessageType;
  messageContent: string;
  agentReferenceId: string;
  setChatMessageContent: (value: React.SetStateAction<string>) => void;
  mediaS3Path?: string;
  mediaType?: string;
  caption?: string | null;
  mediaUrl?: string;
  fileName?: string;
  fileSize?: number;
}

export interface IEnrichCustomerSelfiePayload {
  customerId: string;
  agentReferenceId: string;
  accountNumber: string;
}

export interface IGetCustomerSelfiePayload {
  customerReferenceId: string;
  loanAccountNumber: string;
}

export const SCROLL_THRESHOLD_TO_DETECT_BOTTOM = 250;

export enum ChatSearchParams {
  customerName = 'customerName',
  loanAccountNumber = 'loanAccountNumber',
  phoneNumber = 'phoneNumber',
  messageContent = 'messageContent'
}

export const chatSearchKeys = [
  ChatSearchParams.customerName,
  ChatSearchParams.loanAccountNumber,
  ChatSearchParams.phoneNumber,
  ChatSearchParams.messageContent
];

export interface AppendNewWhatsappChatbotMessagePayload {
  referenceId: string;
  messages: Array<WhatsappChatbotMessages>;
}

export const quickFiltersIconColor = '#0276FE';
export const quickFiltersChevronIconProps = { color: quickFiltersIconColor, width: 20, height: 20 };

export interface IMediaData {
  media_url: string;
  caption: string | null;
  mime_type: string;
  summary: string;
}

export interface IMediaContent {
  mediaUrl: string;
  caption: string;
  mimeType: string;
  summary: string;
  fileName?: string;
  fileSize?: number;
}

export interface IImageData {
  mediaUrl: string;
  mediaS3Path: string;
  mediaType: 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/heic' | 'image/heif';
  fileName: string;
  fileSize: number;
  fileExtension: string;
}

export interface ICosmosSyncData {
  agentReferenceId: string;
  appVersion: string;
  lastSyncTimestamp: number;
  turnedOffPermissions: Array<string>;
  allPermissionsGranted: boolean;
}

export interface ChatListItemsProps {
  searchQuery: string;
  chatsCount: number;
}

export interface ChatRowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    filteredChatsList: IChatListItem[];
    onChatItemClickHandler: (chatItem: IChatListItem) => void;
    chatRefId?: string;
  };
}
